import { useRef, useState } from 'react';

// third-party
import { FormattedMessage, useIntl } from 'react-intl';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, ClickAwayListener, List, ListItemButton, ListItemIcon, ListItemText, Paper, Popper, Typography } from '@mui/material';

// project-imports
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import useConfig from 'hooks/useConfig';
import useSystemColorScheme from 'hooks/useSystemColorScheme';

// assets
import { Monitor, Moon, Sun1 } from 'iconsax-react';

// types
import { ThemeMode } from 'types/config';

// ==============================|| HEADER CONTENT - THEME MODE ||============================== //
//
// The template put this in a full customisation drawer alongside RTL, preset colours, fonts and
// menu layout. None of the rest is a decision this dashboard's users need to make, so only the one
// setting that matters — reading it at night — is surfaced, one click from anywhere.
//
// The choice is stored in localStorage by ConfigContext, so it survives a reload.

const OPTIONS = [
  { value: ThemeMode.LIGHT, labelId: 'theme.light', icon: Sun1 },
  { value: ThemeMode.DARK, labelId: 'theme.dark', icon: Moon },
  { value: ThemeMode.AUTO, labelId: 'theme.auto', icon: Monitor }
];

const ThemeModeToggle = () => {
  const theme = useTheme();
  const intl = useIntl();
  const { mode, onChangeMode } = useConfig();
  const systemPrefersDark = useSystemColorScheme();

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);

  // The button shows what is actually on screen, not the stored setting: under "auto" a moon here
  // would be a lie on a light desktop.
  const effectiveDark = mode === ThemeMode.AUTO ? systemPrefersDark : mode === ThemeMode.DARK;
  const ActiveIcon = effectiveDark ? Moon : Sun1;

  const handleSelect = (next: ThemeMode) => {
    onChangeMode(next);
    setOpen(false);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.5 }}>
      <IconButton
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
        color="secondary"
        variant="light"
        aria-label={intl.formatMessage({ id: 'theme.toggle' })}
        sx={{ color: 'secondary.main', bgcolor: open ? 'secondary.200' : 'secondary.100' }}
      >
        <ActiveIcon variant="Bulk" size={20} />
      </IconButton>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={{ boxShadow: theme.customShadows.z1, width: 220 }}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MainCard content={false} border={false}>
                  <List component="nav" sx={{ p: 1, '& .MuiListItemIcon-root': { minWidth: 32 } }}>
                    {OPTIONS.map((option) => {
                      const Icon = option.icon;

                      return (
                        <ListItemButton key={option.value} selected={mode === option.value} onClick={() => handleSelect(option.value)}>
                          <ListItemIcon>
                            <Icon variant="Bulk" size={18} />
                          </ListItemIcon>
                          <ListItemText primary={intl.formatMessage({ id: option.labelId })} />
                        </ListItemButton>
                      );
                    })}
                  </List>

                  {mode === ThemeMode.AUTO && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', px: 2, pb: 1.5 }}>
                      <FormattedMessage id={systemPrefersDark ? 'theme.nowDark' : 'theme.nowLight'} />
                    </Typography>
                  )}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default ThemeModeToggle;
